import classNames from 'classnames';
import { ReactNode } from 'react';
import { MaterialIconName } from '../../types/materialIconName';
import { CmsImage } from '../CmsImage';
import { MaterialIcon } from '../MaterialIcon';
import { CC_CARD_TEXT } from './utils';

type CcProductCardRewardRowProps = {
  /** MoneyHero leads; the bank's row is visually secondary (FR-1.5.9). */
  tone: 'moneyhero' | 'bank';
  icon: MaterialIconName;
  /** The gift's own picture, in place of the icon, as apply-v3's reward cards show it. */
  image?: string;
  eyebrow: string;
  /** Inline after the eyebrow: the urgency pill (FR-1.5.8). */
  eyebrowAside?: ReactNode;
  title?: string;
  /** Under the title, e.g. a top-up price (FR-1.5.6). */
  note?: string;
  value?: string;
  /** Under the title: what opening the row shows, "+5 more reward options". */
  children?: ReactNode;
  /** At the far right: the chevron of a row that opens. */
  trailing?: ReactNode;
};

/** One reward line: the gift's picture or an icon, what the reader gets, and its value at right. */
export const CcProductCardRewardRow: React.FC<CcProductCardRewardRowProps> = ({
  tone,
  icon,
  image,
  eyebrow,
  eyebrowAside,
  title,
  note,
  value,
  children,
  trailing,
}) => {
  const isMoneyHero = tone === 'moneyhero';
  // The bank's line is the smaller one, in type as in its tile.
  const text = isMoneyHero ? CC_CARD_TEXT.gift : CC_CARD_TEXT.value;

  return (
    <div
      data-component="cc-product-card-reward-row"
      className="flex items-center gap-2.5"
    >
      {image ? (
        <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-radius/button bg-fill/card">
          <CmsImage
            src={image}
            alt=""
            width={40}
            height={40}
            sizes={{ _: 40 }}
            className="size-full object-contain"
          />
        </span>
      ) : (
        <span
          aria-hidden="true"
          className={classNames(
            'flex shrink-0 items-center justify-center rounded-radius/button text-text/on-dark',
            isMoneyHero
              ? 'size-7 bg-buttons/cta'
              : 'size-5 bg-fill/primary-dark',
          )}
        >
          <MaterialIcon icon={icon} px={isMoneyHero ? 16 : 12} />
        </span>
      )}
      <div
        className={classNames(
          'flex min-w-0 grow flex-col',
          // The prototype sets the bank's eyebrow closer to its line.
          isMoneyHero ? 'gap-spacing/gap/soft-4px' : 'gap-0.5',
        )}
      >
        <div className="flex flex-wrap items-center gap-x-spacing/gap/soft-6px gap-y-spacing/gap/soft-4px">
          <span
            className={classNames(
              CC_CARD_TEXT.eyebrow,
              isMoneyHero
                ? 'text-highlights/orange-dark'
                : 'text-fill/primary-dark',
            )}
          >
            {eyebrow}
          </span>
          {eyebrowAside}
        </div>
        {title && (
          // Never clipped: an offer's title can be a whole sentence (a bank's welcome offer often is).
          <span className={classNames(text, 'text-text/primary')}>{title}</span>
        )}
        {note && (
          <span
            className={classNames(
              CC_CARD_TEXT.note,
              'text-text/secondary-lighter',
            )}
          >
            {note}
          </span>
        )}
        {children}
      </div>
      {value && (
        <span className={classNames(text, 'shrink-0 text-text/primary')}>
          {value}
        </span>
      )}
      {trailing}
    </div>
  );
};
